import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  id: "wrapper",
  "data-screen-name": "SupersetScreen",
  "data-testid": "superset.designer"
}
const _hoisted_2 = { class: "screen designer-screen screenForm" }
const _hoisted_3 = { class: "portlet light !mb-0 !p-0" }
const _hoisted_4 = { class: "flex flex-col gap-4.5 designer-screen-container" }
const _hoisted_5 = { class: "actionBarSubForm sps-card shadow-md shadow-black/5 border-0 !mb-0" }
const _hoisted_6 = { role: "form" }
const _hoisted_7 = { class: "form-body" }
const _hoisted_8 = { class: "action-bar-wrapper gap-2" }
const _hoisted_9 = { class: "bar-left" }
const _hoisted_10 = { class: "flex items-center gap-[5px] relative screen-branding-wrapper" }
const _hoisted_11 = { class: "flex items-center gap-1 superset-nav" }
const _hoisted_12 = { class: "bar-right" }
const _hoisted_13 = { class: "w-full flex items-center justify-end action-controls-container" }
const _hoisted_14 = { class: "flex flex-wrap justify-end gap-1 actionBar-Controls" }
const _hoisted_15 = { class: "middle-block" }
const _hoisted_16 = { class: "sps-card" }
const _hoisted_17 = { class: "sps-card-content py-1 px-3" }

import { onMounted, onUnmounted, ref } from 'vue';

import { Notifier } from 'Core/Common/Notifier';
import { LOCAL_STORAGE } from 'Core/Common/Enums/LocalStorageItems';
import { SupersetStore } from 'Pages/BI/Stores/SupersetStore';
import SupersetAssistant from 'Pages/BI/Vue/SupersetAssistant.vue';

type Section = 'dashboards' | 'charts' | 'datasets';


export default /*@__PURE__*/_defineComponent({
  __name: 'SupersetDesigner',
  setup(__props) {

const frame = ref<HTMLIFrameElement | null>(null);
const isLoading = ref(false);
const section = ref<Section>('dashboards');
const currentDashboardId = ref('');
const assistantOpen = ref(true);
const assistantModel = ref('');
// Stays false until the settings call confirms the assistant can run, so the
// chat never appears on an instance where it could only fail.
const assistantEnabled = ref(false);
const context = ref<{ kind: string; id: string; label: string }>({
    kind: 'dashboards',
    id: '',
    label: 'the dashboard list',
});
const rootUrl = ref('');
// Where Superset actually is, so a refresh can reload THAT page.
const currentHref = ref('');

// Superset posts its own location on every navigation (see the beacon in
// superset_config.py), which is the only way to know where the user went
// inside a cross-origin iframe.
const readContext = (href: string): { kind: string; id: string; label: string } => {
    const path = href.replace(/^https?:\/\/[^/]+/, '');

    const dashboard = /\/superset\/dashboard\/([^/?#]+)/.exec(path);
    if (dashboard) {
        const editing = /[?&]edit=true/.test(path);
        return {
            kind: editing ? 'dashboard-designer' : 'dashboard',
            id: dashboard[1],
            label: `${editing ? 'editing dashboard' : 'dashboard'} ${dashboard[1]}`,
        };
    }

    const explore = /[?&]slice_id=(\d+)/.exec(path);
    if (explore || path.startsWith('/explore')) {
        const id = explore ? explore[1] : '';
        return {
            kind: 'chart-editor',
            id,
            label: id ? `the chart editor for chart ${id}` : 'the chart editor',
        };
    }

    if (path.startsWith('/sqllab')) return { kind: 'sqllab', id: '', label: 'SQL Lab' };
    if (path.startsWith('/chart/list')) return { kind: 'charts', id: '', label: 'the chart list' };
    if (path.startsWith('/tablemodelview/list') || path.startsWith('/dataset'))
        return { kind: 'datasets', id: '', label: 'the dataset list' };
    if (path.startsWith('/dashboard/list')) return { kind: 'dashboards', id: '', label: 'the dashboard list' };

    return { kind: 'superset', id: '', label: 'Superset' };
};

const onBeacon = (event: MessageEvent) => {
    if (!rootUrl.value || event.origin !== rootUrl.value) {
        return;
    }
    const data: any = event.data;
    if (!data || data.source !== 'spheeres-superset' || typeof data.href !== 'string') {
        return;
    }

    currentHref.value = data.href;
    context.value = readContext(data.href);
    // A dashboard the user opened INSIDE Superset counts as the open one, so
    // the share link and the assistant follow along.
    currentDashboardId.value = context.value.kind.startsWith('dashboard') ? context.value.id : '';
    if (['charts', 'datasets', 'dashboards'].includes(context.value.kind)) {
        section.value = context.value.kind as Section;
    }
};

// Superset owns the page inside the frame; the platform only decides which of
// its screens to hand over, always through a fresh single-use autologin url.
const sectionLabels: Record<Section, string> = {
    dashboards: 'the dashboard list',
    charts: 'the chart list',
    datasets: 'the dataset list',
};

const load = (dashboardId?: string, target?: Section) => {
    if (target) {
        section.value = target;
    }
    currentDashboardId.value = dashboardId || '';
    // Optimistic context: the beacon confirms once the new frame loads, but
    // until then neither the prompt nor a refresh may act on the page we are
    // leaving - the prompt prefers the label, so a stale label would win.
    currentHref.value = '';
    context.value = dashboardId
        ? { kind: 'dashboard', id: dashboardId, label: `dashboard ${dashboardId}` }
        : { kind: section.value, id: '', label: sectionLabels[section.value] };
    isLoading.value = true;

    SupersetStore.GetDesignerUrl(dashboardId, section.value)
        .always(() => {
            isLoading.value = false;
        })
        .then((url: string) => {
            if (frame.value) {
                frame.value.src = url;
            }
        });
};

const openSection = (target: Section) => load(undefined, target);

// The assistant can put any Superset page on screen; the frame's session is
// already established, so a relative path is enough. Only same-site relative
// paths are accepted - anything else is ignored.
const openPath = (path: string) => {
    if (!frame.value || !rootUrl.value || !path.startsWith('/') || path.startsWith('//')) {
        return;
    }
    currentHref.value = rootUrl.value + path;
    context.value = readContext(path);
    currentDashboardId.value = context.value.kind.startsWith('dashboard') ? context.value.id : '';
    frame.value.src = currentHref.value;
};

// After the assistant changes something, reload the page the user is on - the
// Superset session is already established, so the url can be used directly.
const refresh = () => {
    if (currentHref.value && frame.value) {
        frame.value.src = currentHref.value;
        return;
    }
    load(currentDashboardId.value || undefined);
};

// The share link an IFrame control expects: the platform endpoint that serves
// the embed page for this dashboard.
const copyLink = () => {
    if (!currentDashboardId.value) {
        return;
    }

    const link = `/api/SupersetApi/GetDashboard?dashboardId=${currentDashboardId.value}`;
    const notify = () => new Notifier().Success(`Dashboard link copied: ${link}`);

    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(link).then(notify, notify);
        return;
    }

    const helper = document.createElement('textarea');
    helper.value = link;
    document.body.appendChild(helper);
    helper.select();
    document.execCommand('copy');
    document.body.removeChild(helper);
    notify();
};

const fullScreen = () => {
    const element = frame.value as any;
    if (!element) {
        return;
    }
    (element.requestFullscreen || element.webkitRequestFullscreen || element.mozRequestFullScreen)?.call(element);
};

onMounted(() => {
    SupersetStore.GetSettings().then((settings: any) => {
        assistantModel.value = settings.AssistantModel || '';
        assistantEnabled.value = !!settings.AssistantEnabled;
        try {
            // A relative RootUrl means the platform proxies Superset under its own
            // origin, which is then exactly where the beacon messages come from.
            rootUrl.value = new URL(settings.RootUrl, window.location.origin).origin;
        } catch {
            rootUrl.value = window.location.origin;
        }

        // Opening this page starts a Superset session that outlives the platform's
        // own, so leave sign-out the address it needs to end it.
        if (settings.RootUrl) {
            localStorage.setItem(LOCAL_STORAGE.SUPERSET_ROOT, settings.RootUrl);
        }
    });
    window.addEventListener('message', onBeacon);
    load();
});

onUnmounted(() => window.removeEventListener('message', onBeacon));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("form", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "screen-logo-box rounded-full" }, [
                        _createElementVNode("span", { class: "icon-content text-left additional-icon custom-color-option fa-stack fa fa-3x fa-solid fa-pie-chart" })
                      ], -1 /* CACHED */)),
                      _cache[7] || (_cache[7] = _createElementVNode("div", { class: "screen-name-box max-w-[200px] truncate" }, [
                        _createElementVNode("span", { class: "font-medium text-lg text-mono" }, "Superset")
                      ], -1 /* CACHED */)),
                      _createElementVNode("div", _hoisted_11, [
                        _createElementVNode("a", {
                          class: _normalizeClass(["text-sm sps-btn sps-btn-ghost superset-nav__link", { 'sps-btn-primary': section.value === 'dashboards' }]),
                          "data-testid": "superset.nav-dashboards",
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (openSection('dashboards')))
                        }, "Dashboards", 2 /* CLASS */),
                        _createElementVNode("a", {
                          class: _normalizeClass(["text-sm sps-btn sps-btn-ghost superset-nav__link", { 'sps-btn-primary': section.value === 'charts' }]),
                          "data-testid": "superset.nav-charts",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (openSection('charts')))
                        }, "Charts", 2 /* CLASS */),
                        _createElementVNode("a", {
                          class: _normalizeClass(["text-sm sps-btn sps-btn-ghost superset-nav__link", { 'sps-btn-primary': section.value === 'datasets' }]),
                          "data-testid": "superset.nav-datasets",
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (openSection('datasets')))
                        }, "Datasets", 2 /* CLASS */)
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        (currentDashboardId.value)
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              class: "text-sm sps-btn sps-btn-dim",
                              "data-testid": "superset.copy-link",
                              title: "Copy the dashboard link for an IFrame control",
                              onClick: copyLink
                            }, [...(_cache[8] || (_cache[8] = [
                              _createElementVNode("i", { class: "fa fa-link" }, null, -1 /* CACHED */)
                            ]))]))
                          : _createCommentVNode("v-if", true),
                        (assistantEnabled.value)
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 1,
                              class: _normalizeClass(["text-sm sps-btn sps-btn-dim", { 'sps-btn-primary': assistantOpen.value }]),
                              "data-testid": "superset.assistant-toggle",
                              title: "Analytics assistant",
                              onClick: _cache[3] || (_cache[3] = ($event: any) => (assistantOpen.value = !assistantOpen.value))
                            }, [...(_cache[9] || (_cache[9] = [
                              _createElementVNode("i", { class: "fa fa-comments" }, null, -1 /* CACHED */)
                            ]))], 2 /* CLASS */))
                          : _createCommentVNode("v-if", true),
                        _createElementVNode("a", {
                          class: "text-sm sps-btn sps-btn-dim",
                          "data-testid": "superset.fullscreen",
                          title: "Full screen",
                          onClick: fullScreen
                        }, [...(_cache[10] || (_cache[10] = [
                          _createElementVNode("i", { class: "fa fa-expand" }, null, -1 /* CACHED */)
                        ]))])
                      ])
                    ])
                  ])
                ])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("div", _hoisted_17, [
                _createElementVNode("iframe", {
                  ref_key: "frame",
                  ref: frame,
                  title: "Superset",
                  "data-testid": "superset.frame",
                  class: "superset-frame"
                }, null, 512 /* NEED_PATCH */)
              ])
            ]),
            (assistantEnabled.value)
              ? _withDirectives((_openBlock(), _createBlock(SupersetAssistant, {
                  key: 0,
                  model: assistantModel.value,
                  context: context.value,
                  "current-dashboard-id": currentDashboardId.value,
                  onClose: _cache[4] || (_cache[4] = ($event: any) => (assistantOpen.value = false)),
                  onOpenDashboard: _cache[5] || (_cache[5] = (id) => load(id)),
                  onOpenPath: openPath,
                  onRefresh: refresh
                }, null, 8 /* PROPS */, ["model", "context", "current-dashboard-id"])), [
                  [_vShow, assistantOpen.value]
                ])
              : _createCommentVNode("v-if", true)
          ])
        ])
      ])
    ])
  ]))
}
}

})